/**
 * Construction system: builders contribute to adjacent foundations; on
 * completion the foundation becomes a functional building.
 */
import type { World } from "../world";
import type { Registry } from "../../data/registry";
import { FIXED_DT } from "../types";
import { advanceOrder } from "../orders";
import { attachFieldResource } from "../spawn";

export function constructionSystem(world: World, reg: Registry): void {
  for (const [eid, b] of world.building) {
    if (!b.isFoundation) {
      b.beingBuilt = false;
      continue;
    }
    const tr = world.transform.get(eid)!;
    const def = reg.building(world.ident.get(eid)!.civ!, world.ident.get(eid)!.defId);
    // A builder is routed to ~0.5·size from a normal foundation's centre, so it
    // must actually ARRIVE there (1.1·size; 2.0 let a house start a tile early).
    // A dock is built from the adjacent SHORE (~1 tile from the water-hex centre),
    // so it gets a full-tile reach instead of demanding the worker stand on water.
    const onWater = world.isWater(b.origin.x, b.origin.y);
    const reach = onWater ? Math.sqrt(3) * world.size * 1.25 : world.size * 1.1;
    let contributors = 0;
    for (const [wid, builder] of world.builder) {
      if (builder.foundation !== eid) continue;
      const wTr = world.transform.get(wid);
      if (!wTr) continue;
      const d = Math.hypot(wTr.pos.x - tr.pos.x, wTr.pos.y - tr.pos.y);
      if (d <= reach) contributors++;
    }
    b.beingBuilt = contributors > 0;
    if (contributors > 0) {
      const rate = (1 / def.buildTime) * Math.min(contributors, 3); // up to 3x
      b.constructionProgress += rate * FIXED_DT;
      const hp = world.health.get(eid)!;
      hp.hp = Math.min(hp.maxHp, Math.max(1, Math.round(hp.maxHp * (0.1 + 0.9 * b.constructionProgress))));
      if (b.constructionProgress >= 1) {
        // complete
        b.constructionProgress = 1;
        b.isFoundation = false;
        b.beingBuilt = false;
        const owner = world.owner.get(eid)!.player;
        const civ = world.ident.get(eid)!.civ!;
        // The finished building registers its routing obstacle — deferred from spawn
        // so a foundation never blocks pathing or its own builder. A WALL is also a
        // sub-tile rampart BAR in movement (movementSystem), so nobody is teleported:
        // a unit on the tile is simply pushed off the bar to the free space.
        const ok = world.hkey(b.origin.x, b.origin.y);
        if (world.isWater(b.origin.x, b.origin.y)) world.solidHexKeys.add(ok);
        else if (b.gate) world.gateHexKeys.set(ok, owner);
        else if (b.solid) world.solidHexKeys.add(ok);
        // enable production
        if (reg.trainedAt(civ, world.ident.get(eid)!.defId).length > 0)
          world.producer.set(eid, { queue: [] });
        // a completed field becomes a harvestable food source
        attachFieldResource(world, reg, eid);
        // drop site registration
        if (b.isDropSite) {
          let set = world.dropsitesByPlayer.get(owner);
          if (!set) {
            set = new Set();
            world.dropsitesByPlayer.set(owner, set);
          }
          set.add(eid);
        }
        // population cap
        if (b.populationCap) {
          const ps = world.players.get(owner);
          if (ps) ps.populationCap += b.populationCap;
        }
        // free workers
        for (const [, builder] of world.builder)
          if (builder.foundation === eid) builder.foundation = null;
        for (const [wid, m] of world.move)
          if (m.intent?.kind === "build" && m.intent.foundation === eid) {
            m.intent = undefined;
            advanceOrder(world, wid); // continue any shift-queued follow-up order
          }
        world.emit({ type: "spawn", tick: 0, at: { ...tr.pos }, color: "#fff" });
      }
    }
  }
}
